/**
 * Right-side panel listing every attempt to fix one test's script: what
 * went wrong, what the healer changed, and whether it worked. Mockup:
 * docs/mockups/2085-fix-history.html.
 *
 * A sibling of `HistoryPanel`, with the same shell, rather than rows inside
 * it. Version history lists git commits, and an attempt that did not work
 * makes no commit, so those attempts could never appear there. A run also
 * saves all of its tests in one commit, so a commit cannot be matched to
 * one attempt.
 *
 * The parent mounts this conditionally and keys it on the attempt count,
 * so a new attempt refetches.
 */
import { useEffect, useState } from 'react'
import { testScriptsAPI, type FixTry } from '@/lib/api'

/** Attempts shown before "Show N earlier tries". Most tests have one or
 * two; a test tried ten times should not push the newest off the screen. */
const SHOWN_AT_FIRST = 3

/** Lines of the error shown before "Show full error". The first lines of
 * a Playwright error say what failed; the rest is call log. */
const ERROR_LINES_SHOWN = 2

export interface FixHistoryPanelProps {
  /** TestScript _id. */
  scriptId: string
  onClose: () => void
}

/** "N tried · M worked". Shared with the Details drawer's Fixes row so the
 * two never disagree. */
export function fixSummary(tried: number, worked: number): string {
  return `${tried} tried · ${worked} worked`
}

export function FixHistoryPanel({ scriptId, onClose }: FixHistoryPanelProps) {
  const [tries, setTries] = useState<FixTry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setTries(null)
    setError(null)
    testScriptsAPI
      .getFixes(scriptId)
      .then((data) => {
        if (!cancelled) setTries(data)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the fix history')
      })
    return () => {
      cancelled = true
    }
  }, [scriptId])

  const worked = tries?.filter((t) => t.wasSuccessful).length ?? 0
  const shown = tries && !showAll ? tries.slice(0, SHOWN_AT_FIRST) : tries ?? []
  const hidden = (tries?.length ?? 0) - shown.length

  return (
    <aside className="w-80 flex-none flex flex-col bg-muted/20 border-l" aria-label="Fix history">
      <div className="flex-none px-3 py-2 border-b flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Fix history</div>
          <div className="text-xs text-muted-foreground">
            {tries ? fixSummary(tries.length, worked) : ' '}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
          aria-label="Close fix history"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!tries && !error && (
          <div className="p-3 text-xs text-muted-foreground">Loading fix history…</div>
        )}
        {error && (
          <div className="p-3 text-xs text-destructive" role="alert">
            {error}
          </div>
        )}
        {tries && tries.length === 0 && (
          <div className="p-3 text-xs text-muted-foreground">This test has never been fixed.</div>
        )}
        <ul className="px-2 py-2 space-y-1.5">
          {shown.map((t) => (
            <FixTryItem key={`${t.runId}-${t.healedAt}`} fixTry={t} />
          ))}
          {hidden > 0 && (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">
              {hidden === 1 ? '1 earlier try' : `${hidden} earlier tries`}{' '}
              <button type="button" className="text-primary hover:underline" onClick={() => setShowAll(true)}>
                Show
              </button>
            </li>
          )}
        </ul>
      </div>
    </aside>
  )
}

function FixTryItem({ fixTry }: { fixTry: FixTry }) {
  const [fullError, setFullError] = useState(false)
  const lines = fixTry.failureReason.trim().split('\n').filter((l) => l.trim() !== '')
  const long = lines.length > ERROR_LINES_SHOWN
  const errorText = long && !fullError ? lines.slice(0, ERROR_LINES_SHOWN).join('\n') : fixTry.failureReason.trim()

  return (
    <li className="rounded-md border bg-card px-2.5 py-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        {fixTry.wasSuccessful ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            Fixed
          </span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
            Didn't work · not saved
          </span>
        )}
        <span className="text-[10px] text-muted-foreground" title={new Date(fixTry.healedAt).toLocaleString()}>
          {new Date(fixTry.healedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        <span className="text-[10px] text-muted-foreground" title={`Run ${fixTry.runId}`}>
          · run {fixTry.runId.slice(0, 4)}
        </span>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">What went wrong</div>
      <pre className="mt-0.5 whitespace-pre-wrap break-words rounded border bg-muted/40 px-1.5 py-1 font-mono text-[11px] leading-snug">
        {errorText}
      </pre>
      {long && (
        <button
          type="button"
          className="text-[11px] text-primary hover:underline"
          onClick={() => setFullError((v) => !v)}
        >
          {fullError ? 'Show less' : 'Show full error'}
        </button>
      )}
      <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">What it changed</div>
      <p className="mt-0.5 whitespace-pre-wrap break-words">{fixTry.changesDescription}</p>
    </li>
  )
}
